import { supabase } from "./supabase";

let checkInInFlight = false;

const serverErrorCodes = new Set([
  "duplicate",
  "window_closed",
  "out_of_range",
  "impossible_jump",
  "rate_limited",
  "unauthenticated",
  "invalid_input",
  "fixture_not_found",
  "server_error",
  // Round 11 (R9) — the stand-in check-in path.
  "standin_not_allowed",
]);

export function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject({ code: "location_unsupported" });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      (error) => {
        const denied = error?.code === error?.PERMISSION_DENIED || error?.code === 1;
        reject({ code: denied ? "location_denied" : "location_unavailable" });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

async function errorResult(error) {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const body = await error.context.json();
      const code = serverErrorCodes.has(body?.error) ? body.error : "server_error";
      return {
        ok: false,
        code,
        ...(code === "duplicate" && body?.check_in_id
          ? { checkInId: body.check_in_id }
          : {}),
      };
    } catch {
      return { ok: false, code: "server_error" };
    }
  }

  return { ok: false, code: "network_error" };
}

/**
 * `standin: true` (R15) — a replay-mode tester flagged with
 * `profiles.location_standin` checks in "from anywhere": the request skips
 * navigator.geolocation entirely and sends `{ fixture_id, standin: true }`;
 * the server uses the venue's own position and returns 403
 * `standin_not_allowed` if the flag or replay mode isn't on.
 */
export async function performCheckIn(fixtureId, { standin = false } = {}) {
  if (checkInInFlight) return { ok: false, code: "in_flight" };
  checkInInFlight = true;

  try {
    let location = null;
    if (!standin) {
      try {
        location = await getCurrentLocation();
      } catch (error) {
        const code = ["location_denied", "location_unavailable", "location_unsupported"].includes(error?.code)
          ? error.code
          : "location_unavailable";
        return { ok: false, code };
      }
    }

    let response;
    try {
      response = await supabase.functions.invoke("check-in", {
        body: standin
          ? { fixture_id: fixtureId, standin: true }
          : { fixture_id: fixtureId, lat: location.lat, lng: location.lng },
      });
    } catch {
      return { ok: false, code: "network_error" };
    }

    if (response.error) return errorResult(response.error);

    return {
      ok: true,
      checkInId: response.data.check_in_id,
      awards: response.data.awards,
      boosterCards: response.data.booster.cards,
      locationStandin: Boolean(response.data.location_standin),
    };
  } catch {
    return { ok: false, code: "network_error" };
  } finally {
    checkInInFlight = false;
  }
}
